import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { ChevronDown, X } from 'lucide-react'
import { CanonicalLoad, CanonicalLoadInput } from '@numeo/types-core'
import { LoadBid } from '@numeo/types'
import { Spinner } from '@/components/ds2'
import { AppThemeProviders } from '@/components/shared/AppThemeProviders'
import { audioService } from '@/utils/audioService'
import { useAppDispatch } from 'hooks'
import { Category } from 'hooks/useNotification'
import { deleteNotification, upsertNotification } from '@/store/notificationSlice'
import { bidLoadId, bidNotificationSecondaryMessage, bidderLabel, fmtBidPrice } from '../utils/bidFormat'
import { useBoxTruckLoadLookup } from '../hooks/useBoxTruckLoadLookup'
import { BidNotificationCardHead } from './BidNotificationCardHead'
import { BoxTruckLoadDrawer } from './BoxTruckLoadDrawer'

/** One live driver-bid card in the bottom-right stack. */
interface BidNotification {
    /** Dedupe key — a re-bid from the same driver on the same load refreshes its card in place. */
    key: string
    bid: LoadBid
    /** The full load the bid is on, when the server rode it along on the WS event — opens the drawer with no
     *  fetch. Absent on a legacy server; the click then falls back to the Numeo-id lookup. */
    load?: CanonicalLoadInput
    /** The driver's PREVIOUS price on this load when this is a re-bid — rendered as "$230 → $300". Undefined
     *  on a first bid (or an unchanged-price re-send). Remembered per key for the whole session, so the
     *  change still shows when the first card already auto-dismissed. */
    prevPrice?: number
}

/** Keep the stack bounded — the oldest cards drop off once this many are showing (newest are kept). */
const MAX_CARDS = 12

/** How long a bid card stays on screen once VISIBLE before it auto-dismisses. The bid itself is upserted
 *  into the NotificationCenter on arrival, so an auto-dismissed card is never lost — the bell keeps it. */
const AUTO_DISMISS_MS = 5000

/** How many cards peek out behind the front one in the collapsed deck (Telegram-style). */
const PEEK_DEPTH = 2

/**
 * App-wide stack of driver-bid notifications for Box Truck operators. Listens for the `box-truck:bid-placed`
 * window event (bridged from the WebSocket handler; the server only fans it out to allowlisted operators, so
 * nobody else ever sees a card).
 *
 * Collapsed, the cards form a Telegram-style deck bottom-right: the newest sits fully visible on top and the
 * older ones peek out a sliver behind it. Clicking the deck fans it into the full list; clicking a card there
 * opens the SAME load-detail drawer the loadboard table uses (fetched by the bid's Numeo id). Each card
 * dismisses independently (its ✕), and a card auto-dismisses AUTO_DISMISS_MS after being visible — the
 * countdown pauses while the list is expanded (operator reading) or a load drawer covers the stack. Every
 * bid is ALSO upserted into the NotificationCenter (the draggable bell) on arrival, so nothing is lost.
 */
export const BoxTruckBidNotifications = () => {
    const dispatch = useAppDispatch()
    const [items, setItems] = useState<BidNotification[]>([])
    const [open, setOpen] = useState(false)
    // The bid whose load-detail drawer is open — its bid is passed to the drawer so the driver's offer shows
    // in the load's Driver Bids section.
    const [openBid, setOpenBid] = useState<LoadBid | null>(null)
    // The load whose drawer is open when it came straight from the WS event (no fetch). The lookup hook below
    // is only the legacy fallback for a server that doesn't send the load.
    const [directLoad, setDirectLoad] = useState<CanonicalLoad | null>(null)
    const { load, loading, notFound, lookup, clear } = useBoxTruckLoadLookup()

    // A one-off "not found" (load expired/removed) shouldn't linger — clear the toast after a few seconds.
    useEffect(() => {
        if (!notFound) return
        const t = window.setTimeout(clear, 3000)
        return () => window.clearTimeout(t)
    }, [notFound, clear])

    const dismiss = useCallback((key: string) => setItems((prev) => prev.filter((n) => n.key !== key)), [])
    const openLoad = useCallback(
        (n: BidNotification) => {
            setOpenBid(n.bid)
            if (n.load) {
                // Full load rode in on the bid event — open the drawer immediately, no fetch (works for any source).
                setDirectLoad(n.load as CanonicalLoad)
            } else {
                // Legacy server without the load: fall back to the SYLECTUS-only Numeo-id lookup.
                setDirectLoad(null)
                lookup(bidLoadId(n.bid))
            }
        },
        [lookup]
    )
    const closeLoad = useCallback(() => {
        clear()
        setDirectLoad(null)
        setOpenBid(null)
    }, [clear])

    // (Below the callbacks it uses.) WS bridge: every incoming bid becomes a bottom-right card AND a
    // NotificationCenter entry (upsert — a re-bid refreshes its entry instead of stacking duplicates), so
    // the bell keeps the bid after the card auto-dismisses. Clicking the bell entry opens the SAME
    // load-detail drawer a bottom card opens (and acknowledges the entry).
    // The last price each driver bid per load this session — so a re-bid renders as ONE updated
    // notification ("$230 → $300"), not a second "new bid", even after the first card auto-dismissed.
    const lastPriceByKey = useRef(new Map<string, number>())
    // Per-card auto-dismiss countdowns, keyed like `items` — managed by the visibility effect below.
    const dismissTimers = useRef(new Map<string, number>())

    useEffect(() => {
        const onBidPlaced = (e: Event) => {
            const detail = (e as CustomEvent<{ bid: LoadBid; load?: CanonicalLoadInput }>).detail
            const bid = detail?.bid
            if (!bid?.externalId || !bid.bidderId) return
            // A teammate operator's own bid (flagged server-side): the event still updates the board's bid
            // overlays elsewhere, but it is NOT a notification — no card, no sound, no bell entry. Operators
            // are pinged for their DRIVERS' bids only.
            if (bid.bidderIsOperator) return
            const wsLoad = detail.load
            void audioService.playSound().catch(() => {})
            const key = `${bid.externalId}::${bid.bidderId}`
            const last = lastPriceByKey.current.get(key)
            const prevPrice = last != null && last !== bid.price ? last : undefined
            lastPriceByKey.current.set(key, bid.price)
            // A re-bid refreshes the card's CONTENT — give it a fresh countdown too (the per-key timer
            // effect only starts timers for keys without one).
            const pendingTimer = dismissTimers.current.get(key)
            if (pendingTimer != null) {
                window.clearTimeout(pendingTimer)
                dismissTimers.current.delete(key)
            }
            setItems((prev) => {
                const others = prev.filter((n) => n.key !== key)
                return [{ key, bid, load: wsLoad, prevPrice }, ...others].slice(0, MAX_CARDS)
            })
            const notificationId = `box-truck-bid:${key}`
            const priceText =
                prevPrice != null ? `${fmtBidPrice(prevPrice, bid.currency)} → ${fmtBidPrice(bid.price, bid.currency)}` : fmtBidPrice(bid.price, bid.currency)
            dispatch(
                upsertNotification({
                    id: notificationId,
                    category: Category.BoxTruck,
                    message: `${bidderLabel(bid, 'A driver')} ${prevPrice != null ? 'updated bid' : 'bid'} ${priceText}`,
                    secondaryMessage: bidNotificationSecondaryMessage(bid, wsLoad),
                    timestamp: Date.now(),
                    type: 'info',
                    topic: 'Box Truck',
                    onClick: () => {
                        dispatch(deleteNotification(notificationId))
                        openLoad({ key, bid, load: wsLoad })
                    },
                })
            )
        }
        window.addEventListener('box-truck:bid-placed', onBidPlaced)
        return () => window.removeEventListener('box-truck:bid-placed', onBidPlaced)
    }, [dispatch, openLoad])

    // The load the drawer renders: the one that rode in on the event, else the lookup fallback's result.
    const drawerLoad = directLoad ?? load

    // The bid-card stack: collapsed deck, expanded list, or nothing when empty. Rendered alongside the
    // load-detail drawer below (so dismissing every card doesn't unmount an open drawer).
    // Hide the stack while a load drawer is opening/open (it would float over the drawer's right column).
    const showStack = !drawerLoad && !loading

    // Auto-dismiss: each visible card leaves AUTO_DISMISS_MS after ITS OWN countdown starts. Timers are
    // tracked per key so an unrelated new bid arriving does NOT reset older cards' countdowns (a busy
    // bidding session would otherwise keep every card alive forever). Paused — all timers cleared — while
    // the operator expands the list (reading) or a drawer hides the stack (never seen); on becoming
    // visible again every card gets a fresh full countdown. Safe to be aggressive: every bid already
    // lives in the NotificationCenter.
    useEffect(() => {
        const timers = dismissTimers.current
        if (!showStack || open) {
            timers.forEach((t) => window.clearTimeout(t))
            timers.clear()
            return
        }
        const liveKeys = new Set(items.map((n) => n.key))
        for (const [key, t] of timers) {
            if (!liveKeys.has(key)) {
                window.clearTimeout(t)
                timers.delete(key)
            }
        }
        for (const n of items) {
            if (!timers.has(n.key)) {
                timers.set(
                    n.key,
                    window.setTimeout(() => {
                        timers.delete(n.key)
                        dismiss(n.key)
                    }, AUTO_DISMISS_MS)
                )
            }
        }
    }, [items, showStack, open, dismiss])
    // Unmount: drop whatever countdowns are still pending.
    useEffect(() => {
        const timers = dismissTimers.current
        return () => {
            timers.forEach((t) => window.clearTimeout(t))
            timers.clear()
        }
    }, [])

    let stack: React.ReactNode = null
    if (showStack && items.length > 0 && !open) {
        // Collapsed deck: newest on top, up to PEEK_DEPTH older cards peeking behind it.
        const deck = items.slice(0, PEEK_DEPTH + 1)
        stack = createPortal(
            <div className="fixed bottom-4 right-4 z-[60] w-80">
                <button
                    type="button"
                    onClick={() => setOpen(true)}
                    aria-label={`Show ${items.length} bid notifications`}
                    className="relative block w-full text-left"
                >
                    {deck.map((n, i) => (
                        <div
                            key={n.key}
                            style={{ transform: `translateY(-${i * 7}px) scale(${1 - i * 0.045})`, opacity: 1 - i * 0.18, zIndex: 30 - i }}
                            className={`origin-bottom overflow-hidden rounded-2xl border border-border bg-surface shadow-overlay ${i === 0 ? 'relative' : 'absolute inset-x-0 bottom-0'}`}
                        >
                            <BidNotificationCardHead bid={n.bid} load={n.load} prevPrice={n.prevPrice} />
                        </div>
                    ))}
                    {items.length > 1 && (
                        <span className="absolute -top-2 right-2 z-40 rounded-full bg-foreground px-1.5 py-0.5 text-[10px] font-bold leading-none text-background shadow-overlay">
                            {items.length}
                        </span>
                    )}
                </button>
            </div>,
            document.body
        )
    } else if (showStack && items.length > 0) {
        // Expanded: the full list — newest first, each dismissable; clicking a card opens its load detail.
        stack = createPortal(
            <div className="pointer-events-none fixed bottom-4 right-4 z-[60] flex max-h-[80vh] w-80 flex-col items-end gap-2 overflow-y-auto">
                <button
                    type="button"
                    onClick={() => setOpen(false)}
                    className="pointer-events-auto flex items-center gap-1 rounded-full bg-surface px-2.5 py-1 text-xs font-medium text-foreground-muted shadow-overlay transition-colors hover:text-foreground"
                >
                    <ChevronDown size={14} /> Collapse
                </button>
                {items.map((n) => (
                    <div
                        key={n.key}
                        className="pointer-events-auto flex w-full items-start overflow-hidden rounded-2xl border border-border bg-surface shadow-overlay"
                    >
                        <button
                            type="button"
                            onClick={() => openLoad(n)}
                            title="Open load details"
                            className="min-w-0 flex-1 text-left transition-colors hover:bg-default"
                        >
                            <BidNotificationCardHead bid={n.bid} load={n.load} prevPrice={n.prevPrice} />
                        </button>
                        <button
                            type="button"
                            onClick={() => dismiss(n.key)}
                            aria-label="Dismiss"
                            className="m-2 rounded-md p-1 text-foreground-muted transition-colors hover:bg-default hover:text-foreground"
                        >
                            <X size={15} />
                        </button>
                    </div>
                ))}
            </div>,
            document.body
        )
    }

    return (
        <>
            {stack}
            {loading &&
                createPortal(
                    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/30">
                        <Spinner />
                    </div>,
                    document.body
                )}
            {notFound &&
                createPortal(
                    <div className="fixed bottom-4 right-4 z-[70] rounded-lg bg-foreground px-3 py-2 text-xs font-medium text-background shadow-overlay">
                        Load no longer available
                    </div>,
                    document.body
                )}
            {/* The SAME drawer the loadboard table opens — bids=[the clicked bid] so the offer shows in context.
                Wrapped in the Material+Joy CssVars providers because this component mounts at the app root, OUTSIDE
                MasterLayout: the drawer's LoadDetails/PrivateLoadMapSection call MUI's useColorScheme, which throws
                without a CssVarsProvider ancestor. */}
            {drawerLoad && (
                <AppThemeProviders>
                    <BoxTruckLoadDrawer load={drawerLoad} bids={openBid ? [openBid] : []} onClose={closeLoad} />
                </AppThemeProviders>
            )}
        </>
    )
}
